import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { accessLocalStorage, getAllOutboundConfigs, deleteOutboundConfig } from '../../../services';
import { hasFeature } from '../../../services/permissions';

import PbxContext from '../../../context/PbxContext';
import '../../../libs/bulma.min.css';
import './OutboundCalls.css';

import Loading from '../../../components/Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faPlus, faEye, faPhone, faChartLine, faSync, faUsers } from '@fortawesome/free-solid-svg-icons';

function OutboundCallsList() {
  const getItensStateGlobal = useContext(PbxContext);
  const { toggleIsChangeFormElements, toggleIsHidden, setItemsSelectedToEdit } = getItensStateGlobal;

  let url = window.location.href;
  url = url.split('/')[2];

  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [configsSelected, setConfigsSelected] = useState([]);
  const [disable, setDisable] = useState(true);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Filtros
  const [filters, setFilters] = useState({
    callType: '',
    activeOnly: true
  });

  const history = useHistory();

  const fetchConfigs = async () => {
    try {
      // Obter dados do usuário para determinar o tenant
      const dataUser = await accessLocalStorage.getUserLocalStorage();
      let filterParams = {
        activeOnly: filters.activeOnly
      };
      
      // Apenas usuários com role = "root" podem especificar tenant via query parameter
      if (dataUser && dataUser.user && dataUser.user.role === 'root' && dataUser.user.idtenant) {
        filterParams.tenant = dataUser.user.idtenant;
      }
      
      // Adicionar filtro de tipo se selecionado
      if (filters.callType) {
        filterParams.callType = filters.callType;
      }
      
      const response = await getAllOutboundConfigs(filterParams);
      if (response && response.success && response.data) {
        setConfigs(response.data);
      } else {
        setConfigs([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Erro ao buscar configurações:', error);
      setConfigs([]);
      setLoading(false);
    }
  };

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');

    const { user: { role } } = dataUser;
    setUserRole(role);

    await fetchConfigs();
  }, [history, filters]);

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  const handleEdit = (config) => {
    setItemsSelectedToEdit(config);
    toggleIsChangeFormElements();
    toggleIsHidden();
    history.push('/config/chamadas/saida/editar');
  };

  const handleView = (config) => {
    setItemsSelectedToEdit(config);
    history.push('/config/chamadas/saida/visualizar');
  };

  const confirmDelete = (config) => {
    setConfigToDelete(config);
    setShowConfirmDelete(true);
  };

  const handleDelete = async () => {
    if (!configToDelete) return;
    
    try {
      const response = await deleteOutboundConfig(configToDelete.id);
      if (response && response.success) {
        // Atualizar lista
        await fetchConfigs();
        setShowConfirmDelete(false);
        setConfigToDelete(null);
      } else {
        alert('Erro ao remover configuração: ' + (response.error || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro ao remover configuração:', error);
      alert('Erro ao remover configuração');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Mapear tipos de chamadas para exibição
  const callTypeLabels = {
    support: 'Suporte',
    emergency: 'Emergência',
    freecall: 'Ligação Gratuita',
    local: 'Local',
    mobile: 'Celular',
    ddd: 'DDD',
    mobileDdd: 'Celular DDD',
    international: 'Internacional'
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="outbound-container">
      <div className="outbound-header">
        <div className="outbound-title-wrapper">
          <svg className="outbound-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <div>
            <h1 className="outbound-title">Chamadas de Saída</h1>
            <p className="outbound-subtitle">Gerenciar configurações de roteamento de chamadas</p>
          </div>
        </div>
        <div className="outbound-actions">
          <Link to="/config/chamadas/saida/grupos" className="btn-refresh">
            <FontAwesomeIcon icon={faUsers} />
            Grupos de Saída
          </Link>
          {hasFeature('config.chamadas.saida', 'create') && (
          <Link to="/config/chamadas/saida/nova" className="btn-new-outbound">
            <FontAwesomeIcon icon={faPlus} />
            Nova Configuração
          </Link>
          )}
        </div>
      </div>

      <div className="filter-card">
        <div className="filter-row">
          <div className="filter-field">
            <label className="filter-label">Tipo de Chamada</label>
            <select 
              className="filter-select"
              value={filters.callType} 
              onChange={(e) => handleFilterChange('callType', e.target.value)}
            >
              <option value="">Todos os tipos</option>
              {Object.entries(callTypeLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-field">
            <label className="filter-label">Status</label>
            <select 
              className="filter-select"
              value={filters.activeOnly} 
              onChange={(e) => handleFilterChange('activeOnly', e.target.value === 'true')}
            >
              <option value={true}>Apenas Ativas</option>
              <option value={false}>Todas</option>
            </select>
          </div>
          
          <div className="filter-field">
            <label className="filter-label">&nbsp;</label>
            <button className="btn-refresh" onClick={fetchConfigs}>
              <FontAwesomeIcon icon={faSync} />
              Atualizar Lista
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : configs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FontAwesomeIcon icon={faPhone} />
          </div>
          <p className="empty-state-title">Nenhuma configuração encontrada</p>
          <p className="empty-state-text">Clique em "Nova Configuração" para criar sua primeira configuração de chamada de saída.</p>
        </div>
      ) : (
        <>
          <div className="outbound-table-wrapper">
            <table className="outbound-table">
              <thead className="outbound-table-header">
                <tr>
                  <th>ID</th>
                  <th>Tenant</th>
                  <th>Tipo</th>
                  <th>Máscara</th>
                  <th>Tronco</th>
                  <th>Código Operadora</th>
                  <th>Remove Dígitos</th>
                  <th>Timeout</th>
                  <th>Status</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((config) => (
                  <tr key={config.id}>
                    <td><strong>#{config.id}</strong></td>
                    <td>
                      <span className="outbound-badge badge-tenant">{config.tenant}</span>
                    </td>
                    <td>
                      <span className={`outbound-badge badge-${config.callType}`}>
                        {callTypeLabels[config.callType] || config.callType}
                      </span>
                    </td>
                    <td>
                      <span className="outbound-badge badge-mask">{config.mask}</span>
                    </td>
                    <td>
                      <span className="outbound-badge badge-trunk">{config.trunkName}</span>
                    </td>
                    <td>
                      {config.ispCode ? (
                        <span className="outbound-badge badge-isp">{config.ispCode}</span>
                      ) : (
                        <span className="outbound-badge badge-light">N/A</span>
                      )}
                    </td>
                    <td>
                      <span className="outbound-badge badge-light">{config.digitRemove}</span>
                    </td>
                    <td>
                      <span className="outbound-badge badge-light">{config.timeout}s</span>
                    </td>
                    <td>
                      <span className={`outbound-badge ${config.active ? 'badge-active' : 'badge-inactive'}`}>
                        {config.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>
                      {new Date(config.createdAt).toLocaleString('pt-BR')}
                    </td>
                    <td>
                      <div className="outbound-actions-cell">
                        <button 
                          className="btn-outbound-action btn-view-outbound" 
                          title="Visualizar"
                          onClick={() => handleView(config)}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button 
                          className="btn-outbound-action btn-edit-outbound" 
                          title="Editar"
                          onClick={() => handleEdit(config)}
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                        {hasFeature('config.chamadas.saida', 'delete') && (
                          <button
                            className="btn-outbound-action btn-delete-outbound"
                            title="Excluir"
                            onClick={() => confirmDelete(config)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="outbound-stats">
            <p className="outbound-stats-text">
              Total: {configs.length} configuração(ões)
            </p>
          </div>
        </>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showConfirmDelete && (
        <div className="modal is-active">
          <div className="modal-background" onClick={() => setShowConfirmDelete(false)}></div>
          <div className="modal-card">
            <header className="modal-card-head has-background-danger">
              <p className="modal-card-title has-text-white">
                <FontAwesomeIcon icon={faTrash} style={{ marginRight: '10px' }} />
                Confirmar Exclusão
              </p>
              <button 
                className="delete" 
                aria-label="close"
                onClick={() => setShowConfirmDelete(false)}
              ></button>
            </header>
            <section className="modal-card-body">
              <p>
                Tem certeza que deseja excluir a configuração <strong>{callTypeLabels[configToDelete?.callType]}</strong> 
                para a máscara <strong>{configToDelete?.mask}</strong>?
              </p>
              <p className="has-text-danger mt-2">
                <strong>⚠️ Esta ação não pode ser desfeita!</strong>
              </p>
            </section>
            <footer className="modal-card-foot">
              <button 
                className="button is-danger" 
                onClick={handleDelete}
              >
                <FontAwesomeIcon icon={faTrash} style={{ marginRight: '8px' }} />
                Sim, Excluir
              </button>
              <button 
                className="button" 
                onClick={() => setShowConfirmDelete(false)}
              >
                Cancelar
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default OutboundCallsList;
